/**
 * Projects Pop-Out Panel Content
 * 
 * Primary entry point to all work.
 * Per spec:
 * - Active project (highlighted)
 * - Recent projects list
 * - Project templates
 * - Primary CTA: New Project
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faPlus,
    faClock,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { projectsApi, type StrategyProject } from '../../../services/api/projectsApi';

interface ProjectsPopOutProps {
    onClose: () => void;
}

export const ProjectsPopOut: React.FC<ProjectsPopOutProps> = ({ onClose }) => {
    const navigate = useNavigate();

    // Fetch recent projects
    const { data: recentProjects, isLoading } = useQuery({
        queryKey: ['projects', 'recent'],
        queryFn: () => projectsApi.getRecentProjects(5),
        staleTime: 30000,
    });

    const handleNewProject = () => {
        navigate('/app?action=new');
        onClose();
    };

    const handleOpenProject = (project: StrategyProject) => {
        navigate(`/app/lab/project/${project._id}`);
        onClose();
    };

    const handleViewAllProjects = () => {
        navigate('/app?action=browse');
        onClose();
    };

    return (
        <div className="p-4 space-y-6">
            {/* New Project CTA */}
            <button
                onClick={handleNewProject}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 transition-colors font-medium"
            >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                New Project
            </button>

            {/* Recent Projects */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Recent</h3>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-white" />
                    </div>
                ) : recentProjects?.projects?.length ? (
                    <div className="space-y-1">
                        {recentProjects.projects.map((project: StrategyProject) => (
                            <button
                                key={project._id}
                                onClick={() => handleOpenProject(project)}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                            >
                                <FontAwesomeIcon
                                    icon={faFolder}
                                    className="h-4 w-4 text-cyan-500/70 group-hover:text-cyan-400"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-300 truncate group-hover:text-white">
                                        {project.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {project.defaultConfig.symbol} · {project.defaultConfig.interval}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                        No projects yet
                    </p>
                )}
            </div>

            {/* View All */}
            <button
                onClick={handleViewAllProjects}
                className="w-full text-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors py-2"
            >
                View all projects
            </button>
        </div>
    );
};

export default ProjectsPopOut;
